import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux';

import _ from 'lodash';
import cx from 'classnames';
import { useDrag, useDrop } from "react-dnd";
import { guid } from 'services/util';
import Truncate from 'react-truncate';

import { updateWidgetContent } from 'actions/editor';

import { Popover, OverlayTrigger, Button } from "react-bootstrap";
import styles from '../menuList.module.scss';
import SortableTree from './sortableTree';

const SortableTreeItem = ({ id, index, parent, menuContent, contentList, content, widgetId, editorSession, onChangeAction, onChangeName, moveCard, findItem }) => {
  const dispatch = useDispatch();
  const inputRef = React.useRef(null);
  const ref = useRef(null)

  const [{ handlerId }, drop] = useDrop({
    accept: 'menu',
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      }
    },
    hover(item, monitor) {
      if (!ref.current || !monitor.isOver({ shallow: true })) {
        return
      }

      const dragId = _.get(monitor.getItem(), 'id');
      const hoverId = id

      if (dragId === hoverId) {
        return
      }

      const hoverBoundingRect = ref.current?.getBoundingClientRect()
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2
      const clientOffset = monitor.getClientOffset()
      const hoverClientY = clientOffset.y - hoverBoundingRect.top

      moveCard(dragId, hoverId, parent)
    }
  })

  const [{ isDragging }, drag, preview] = useDrag({
    type: 'menu',
    item: () => {
      return { id, index }
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  })

  const opacity = isDragging ? 0 : 1
  drag(drop(ref))

  const [editable, setEditable] = useState(false);
  const [expand, setExpand] = useState(true);
  const [inputName, setInputName] = useState(content.name);

  const handleAddingChild =(parent)=> {
    var updatedContentList = contentList.map((data, index)=>{
      let childrenData = _.get( data, 'children', []) || [];
      
      if(data.uid === parent) {
        return {
          ...data,
          children:[
            ...childrenData,
            {
              uid: guid(),
              name: 'Untitled',
              action: null,
              children: null
            }
          ]
        }
      }

      return data;
    })

    dispatch(updateWidgetContent(editorSession, widgetId, updatedContentList))
  }

  const handleDelete = (selectedMenu) => {
    if (!selectedMenu) {
      return;
    }

    const updatedValue = removeNestedData(contentList, selectedMenu);
    dispatch(updateWidgetContent(editorSession, widgetId, updatedValue))
  }

  useEffect(() => {
    if (editable) {
      inputRef.current.focus();
    }
  }, [editable]);

  const removeNestedData = (menuItems, removingMenu) => {
    return menuItems.filter(content => content.uid !== removingMenu).map(submenu => {
      return { ...submenu, children: removeNestedData(submenu.children || [], removingMenu) }
    });
  }

  const popover = (
    <Popover key={content.uid} bsPrefix="popover" className={styles.pageSettingsPopover}>
      <Popover.Body className={styles.pageSettingsPopoverBody}>
        <div className={styles.pageSettingsPopoverContent}>
          { !parent && <span onClick={() => { handleAddingChild(content.uid) }}>Add Item</span>}
          <span onClick={() => { setEditable(true) }}>Rename</span>
          <span onClick={() => { handleDelete(content.uid) }} >Delete</span>
        </div>
      </Popover.Body>
    </Popover>
  );

  const handleNameChange = (e) => {
    setInputName(e.target.value);
  }

  const updateContentName = (uid) => {
    let updatedMenuItems = onChangeName(contentList, uid, 'name', _.isEmpty(inputName) ? 'Untitled' : inputName)

    dispatch(updateWidgetContent(editorSession, widgetId, updatedMenuItems))
    setEditable(false);
  }

  let showLinkAction = !content.children || (content.children || []).length < 1

  return (
    <div ref={ref} style={{ opacity }} data-handler-id={handlerId} className={cx(styles.menuWrapper)} >
      <div onDoubleClick={() => { setEditable(true) }} ref={preview} className={cx(styles.menuItem, { [styles.childMenuItem]: parent })}>
        <Handle />
        {editable &&
          <input
            ref={inputRef}
            type={'text'}
            className={styles.name}
            value={inputName}
            onChange={handleNameChange}
            onBlur={() => updateContentName(content.uid)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                updateContentName(content.uid);
              }
            }}
          />
        }

        {!editable &&
          <span className={styles.name}>
            <Truncate lines={1}>{content.name}</Truncate>
          </span>
        }
        { content.children && content.children.length > 0 &&
          <Button variant='info' onClick={() => setExpand(!expand)} className={styles.dropDownButton}><i className={cx('cleverstory-drop-down')} /></Button>
        }

        { showLinkAction && 
          <Button variant='info' onClick={() => onChangeAction(content)} className={styles.menuactionButton}><i className={cx('cleverstory-link')} /></Button>
        }

        <OverlayTrigger
          rootClose={true}
          rootCloseEvent="mousedown"
          key={content.uid}
          trigger="click"
          placement="right"
          overlay={popover}
        >
          <i className={cx('cleverstory-more', styles.iconMore)} />
        </OverlayTrigger>
      </div>
      {!parent &&
        <SortableTree
          parent={content.uid}
          show={expand}
          menuContent={_.get(content, 'children', []) || []}
          contentList={contentList}
          onChangeName={onChangeName}
          onChangeAction={onChangeAction}
          widgetId={widgetId}
          editorSession={editorSession}
          moveCard={moveCard}
          findItem={findItem}
        />}
    </div>
  )
};

const Handle = React.forwardRef((props, ref) => {
  return (
    <div className={styles.handle} {...props} ref={ref}>
      <div className={styles.line}>
        {_.times(2).map((index) => {
          return <span key={index} />;
        })}
      </div>
      <div className={styles.line}>
        {_.times(2).map((index) => {
          return <span key={index} />;
        })}
      </div>
      <div className={styles.line}>
        {_.times(2).map((index) => {
          return <span key={index} />;
        })}
      </div>
    </div>
  );
});


export default SortableTreeItem